// Copyright (c) Docugami, Inc. All rights reserved.

import { useChunkSelection } from "hooks/chunkSelection";
import { useSlotForChunk } from "hooks/chunkSlots";
import { useEffect, useMemo, useState } from "react";
import { useDrag } from "react-dnd";
import { DragWrapper } from "./CardStyles";
import { ItemSlots } from "./ItemSlots";
import styled from "styled-components";
import { getEmptyImage } from "react-dnd-html5-backend";
import { isChunkSlot, ISlotItem } from "./ItemSlot";
import { ISuggestedChunk } from "hooks/WorkPathSuggestedChunks";
import { DndChunk, DndType } from "./DndTypes";
import { ItemCardlet } from "./ItemCardlet";
import { ReportItemHeader } from "./ReportItemCardlet";
import { ExpandedRichTextModal } from "./ExpandedRichTextModal";
import { useRichText } from "hooks/chunkText";
import { useExpandedRichTextCommand } from "hooks/ExpandedRichTextCommand";

const Body = styled.div`
    display: flex;
    flex: 1;
    gap: 5px;
    flex-direction: column;
`

const SuggestedItemHeaderContainer = styled.div`
    :hover {
        cursor: pointer;
    }
`;

export interface SuggestedItemCardletProps {
    clusterId: string;
    documentId: string;
    suggestion: ISuggestedChunk;
    footer?: JSX.Element;
    dndDisabled?: boolean;
}

export const ReportSuggestedItemCardlet = (props: SuggestedItemCardletProps): JSX.Element => {
    const { selectChunk } = useChunkSelection();

    const instance = props.suggestion.chunkInstance;
    const label = props.suggestion.label;

    const html = useRichText(instance?.spanId);
    const expandedRichTextCommand = useExpandedRichTextCommand({ richText: html, label: label });

    const [collapsed, setCollapsed] = useState(true)

    function setSelectedChunk(item: ISlotItem | null) {
        if (!!item && isChunkSlot(item)) {
            selectChunk(item.id);
        }
    }

    const slotItem = useSlotForChunk(instance?.spanId);
    const slots = useMemo(() => !!slotItem ? [slotItem] : [], [slotItem]);

    const [{ isDragging }, dragRef, dragVisualRef] = useDrag<DndChunk, any, { isDragging: boolean }>({
        type: DndType.Chunk,
        item: {
            type: DndType.Chunk,
            instance: instance,
            label: label
        },
        canDrag: () => !props.dndDisabled,
        collect: m => ({
            isDragging: m.isDragging()
        })
    }, [instance, props.dndDisabled]);

    useEffect(() => {
        dragVisualRef(getEmptyImage());
    }, [dragVisualRef, instance])

    function getBody(): JSX.Element {
        return (
            <Body>
                <ItemSlots
                    hideManagementCommands={true}
                    slots={slots}
                    onClick={setSelectedChunk} />
            </Body>
        )
    }

    const expandCardlet = () => {
        const updatedCollapse = !collapsed;

        setCollapsed(!collapsed)

        if (!updatedCollapse) {
            setSelectedChunk(slotItem)
        }
    }

    const header = (
        <SuggestedItemHeaderContainer onClick={expandCardlet} >
            <ReportItemHeader label={label} />
        </SuggestedItemHeaderContainer>
    )

    return (
        <DragWrapper isDragging={isDragging}>
            <ItemCardlet
                dragHandleRef={dragRef}
                header={header}
                footer={props.footer}
                collapsed={collapsed}>
                {getBody()}
                <ExpandedRichTextModal command={expandedRichTextCommand} />
            </ItemCardlet>
        </DragWrapper>
    );
}
